import { FilterMatchMode } from "primereact/api";
import { getTranslationObject } from "../../services/Translation";
import { SharedDefectSummaryTableFields } from "./SharedDefectSummaryTableFields";
import { Column } from "primereact/column";
import { 
    defectSummaryActionMenuTemplate,
    sharedDefectSummaryActionMenuTemplate,
    defectSummaryInspectionSinceTemplate,
    defectSummaryPriorityLinkTemplate,
    sharedDefectSummaryPriorityLinkTemplate,
    defectSummaryStatusLinkTemplate,
    sharedDefectSummaryStatusLinkTemplate,
    defectSummaryTotalDefectsLinkTemplate,
    sharedDefectSummaryTotalDefectsLinkTemplate,
    vesselNameTemplate,
} from "./BodyTemplates";
import { DefectPriority } from "../../types/DefectPriority";
import { DefectStatus } from "../../types/DefectStatus";

const t = getTranslationObject();

export const defaultDefectsSummaryFilterConfig = {
    "global": { value: null, matchMode: FilterMatchMode.CONTAINS },
	[SharedDefectSummaryTableFields.VESSEL_NAME]: { value: null, matchMode: FilterMatchMode.IN },
};

export const keywordSearchFilterFields = [
    SharedDefectSummaryTableFields.VESSEL_NAME,
    SharedDefectSummaryTableFields.IMO,
    SharedDefectSummaryTableFields.HIGH_PRIORITY_COUNT,
    SharedDefectSummaryTableFields.MEDIUM_PRIORITY_COUNT,
    SharedDefectSummaryTableFields.LOW_PRIORITY_COUNT,
    SharedDefectSummaryTableFields.INSPECTED,
    SharedDefectSummaryTableFields.TECHNICAL_MANAGER,
    SharedDefectSummaryTableFields.VESSEL_TYPE
];

export const getSharedDefectsSummaryStaticHeaders = (isTechManager: boolean) => {
    const columns = [
        <Column
            key={SharedDefectSummaryTableFields.VESSEL_NAME}
            field={SharedDefectSummaryTableFields.VESSEL_NAME}
            header={t("defectsSummary.vessel")}
            headerClassName={`defects-summary-large-column ${isTechManager ? 'defects-summary-right-border' : ''}`}
            frozen
            sortable
            rowSpan={2}
        />
    ];

    return columns;
}

export const getSharedDefectsSummaryHeaders = (isTechManager: boolean) => {
    const columns = [
        <Column
            key={SharedDefectSummaryTableFields.TOTAL_DEFECTS}
            field={SharedDefectSummaryTableFields.TOTAL_DEFECTS}
            header={t("defectsSummary.totalDefects")}
            headerClassName="defects-summary-small-column defects-summary-right-border"
            sortable
            rowSpan={2}
        />,
        <Column
            key={SharedDefectSummaryTableFields.PRIORITY}
            field={SharedDefectSummaryTableFields.PRIORITY}
            header={t("defectsSummary.priority")}
            headerClassName="defects-summary-no-bottom-border defects-summary-right-border"
            rowSpan={1}
            colSpan={3}
        />,
        <Column
            key={SharedDefectSummaryTableFields.STATUS}
            field={SharedDefectSummaryTableFields.STATUS}
            header={t("defectsSummary.status")}
            headerClassName="defects-summary-no-bottom-border defects-summary-right-border"
            rowSpan={1}
            colSpan={4}
        />,
        <Column
            key={SharedDefectSummaryTableFields.INSPECTED}
            field={SharedDefectSummaryTableFields.INSPECTED}
            header={t("defectsSummary.inspected")}
            headerClassName="defects-summary-large-column"
            sortable
            rowSpan={2}
        />
    ];

    columns.push(
        <Column
            key={SharedDefectSummaryTableFields.VESSEL_TYPE}
            field={SharedDefectSummaryTableFields.VESSEL_TYPE}
            header={t("defectsSummary.vesselType")}
            headerClassName="defects-summary-large-column"
            sortable
            rowSpan={2}
        />
    );

    return columns;
};

export const getSharedDefectsSummarySubHeaders = () => ([
    <Column
        key={SharedDefectSummaryTableFields.HIGH_PRIORITY_COUNT}
        field={SharedDefectSummaryTableFields.HIGH_PRIORITY_COUNT}
        header={t("defectsSummary.highPriority")}
        headerClassName="defects-summary-small-column"
        sortable
    />,
    <Column
        key={SharedDefectSummaryTableFields.MEDIUM_PRIORITY_COUNT}
        field={SharedDefectSummaryTableFields.MEDIUM_PRIORITY_COUNT}
        header={t("defectsSummary.mediumPriority")}
        headerClassName="defects-summary-small-column"
        sortable
    />,
    <Column
        key={SharedDefectSummaryTableFields.LOW_PRIORITY_COUNT}
        field={SharedDefectSummaryTableFields.LOW_PRIORITY_COUNT}
        header={t("defectsSummary.lowPriority")}
        headerClassName="defects-summary-small-column defects-summary-right-border"
        sortable
    />,
    <Column
        key={SharedDefectSummaryTableFields.CLOSED_STATUS}
        field={SharedDefectSummaryTableFields.CLOSED_STATUS}
        header={t("defectsSummary.statusClosed")}
        headerClassName="defects-summary-small-column"
        sortable
    />,
    <Column
        key={SharedDefectSummaryTableFields.RECTIFIED_STATUS}
        field={SharedDefectSummaryTableFields.RECTIFIED_STATUS}
        header={t("defectsSummary.statusRectified")}
        headerClassName="defects-summary-small-column"
        sortable
    />,
    <Column
        key={SharedDefectSummaryTableFields.IN_PROGRESS_STATUS}
        field={SharedDefectSummaryTableFields.IN_PROGRESS_STATUS}
        header={t("defectsSummary.statusInProgress")}
        headerClassName="defects-summary-small-column"
        sortable
    />,
    <Column
        key={SharedDefectSummaryTableFields.OPEN_STATUS}
        field={SharedDefectSummaryTableFields.OPEN_STATUS}
        header={t("defectsSummary.statusOpen")}
        headerClassName="defects-summary-small-column defects-summary-right-border"
        sortable
    />,
]);

export const getSharedDefectsSummaryColumns = (isTechManager: boolean) => {
    const columns = [
        <Column
            key={SharedDefectSummaryTableFields.TOTAL_DEFECTS}
            field={SharedDefectSummaryTableFields.TOTAL_DEFECTS}
            className="text-center defects-summary-right-border"
            body={sharedDefectSummaryTotalDefectsLinkTemplate}
        />,
        <Column
            key={SharedDefectSummaryTableFields.HIGH_PRIORITY_COUNT}
            field={SharedDefectSummaryTableFields.HIGH_PRIORITY_COUNT}
            body={sharedDefectSummaryPriorityLinkTemplate(DefectPriority.high)}
        />,
        <Column
            key={SharedDefectSummaryTableFields.MEDIUM_PRIORITY_COUNT}
            field={SharedDefectSummaryTableFields.MEDIUM_PRIORITY_COUNT}
            body={sharedDefectSummaryPriorityLinkTemplate(DefectPriority.medium)}
        />,
        <Column
            key={SharedDefectSummaryTableFields.LOW_PRIORITY_COUNT}
            field={SharedDefectSummaryTableFields.LOW_PRIORITY_COUNT}
            className="defects-summary-right-border"
            body={sharedDefectSummaryPriorityLinkTemplate(DefectPriority.low)}
        />,
        <Column
            key={SharedDefectSummaryTableFields.CLOSED_STATUS}
            field={SharedDefectSummaryTableFields.CLOSED_STATUS}
            body={sharedDefectSummaryStatusLinkTemplate(DefectStatus.closed)}
        />,
        <Column
            key={SharedDefectSummaryTableFields.RECTIFIED_STATUS}
            field={SharedDefectSummaryTableFields.RECTIFIED_STATUS}
            body={sharedDefectSummaryStatusLinkTemplate(DefectStatus.rectified)}
        />,
        <Column
            key={SharedDefectSummaryTableFields.IN_PROGRESS_STATUS}
            field={SharedDefectSummaryTableFields.IN_PROGRESS_STATUS}
            body={sharedDefectSummaryStatusLinkTemplate(DefectStatus.inProgress)}
        />,
        <Column
            key={SharedDefectSummaryTableFields.OPEN_STATUS}
            field={SharedDefectSummaryTableFields.OPEN_STATUS}
            className="defects-summary-right-border"
            body={sharedDefectSummaryStatusLinkTemplate(DefectStatus.open)}
        />,
        <Column
            key={SharedDefectSummaryTableFields.INSPECTED}
            field={SharedDefectSummaryTableFields.INSPECTED}
            body={defectSummaryInspectionSinceTemplate}
        />
    ];

    columns.push(
        <Column
            key={SharedDefectSummaryTableFields.VESSEL_TYPE}
            field={SharedDefectSummaryTableFields.VESSEL_TYPE}
        />
    );

    return columns;
};


export const getSharedDefectsSummaryStaticColumns = (isTechManager: boolean) => {
    const columns = [
        <Column
            key={SharedDefectSummaryTableFields.VESSEL_NAME}
            field={SharedDefectSummaryTableFields.VESSEL_NAME}
            className={`justify-content-center ${isTechManager ? 'defects-summary-right-border' : ''}`}
            body={vesselNameTemplate}
            frozen
        />
    ];

    return columns;
};
